import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import {
  CouplingDeleteScope,
  ICouplingAssignmentRow,
  IDriverAssignmentComposition,
  IDriverAssignmentListItem,
  IEligibleDriver,
  IUnassignedSummary,
} from 'app/shared/model/driver-assignment.model';
import { extractErrorKey } from '../assignment-ui.utils';

// State of the driver-classification assignment screen (`state.driverAssignment.*`).
const apiUrl = 'api/driver-assignments';

export interface DriverAssignmentState {
  eligibleDrivers: IEligibleDriver[];
  loadingDrivers: boolean;
  errorKey: string | null;
  errorMessage: string | null;
  loading: boolean;
  entity: IDriverAssignmentListItem | null;
  updating: boolean;
  updateSuccess: boolean;
  // Unified coupling screen.
  rows: ICouplingAssignmentRow[];
  rowsTotal: number;
  loadingRows: boolean;
  maxImplementCount: number;
  row: ICouplingAssignmentRow | null;
  loadingRow: boolean;
  summary: IUnassignedSummary | null;
  loadingSummary: boolean;
  summaryError: boolean;
  savingCoupling: boolean;
  saveCouplingSuccess: boolean;
}

const initialState: DriverAssignmentState = {
  eligibleDrivers: [],
  loadingDrivers: false,
  errorKey: null,
  errorMessage: null,
  loading: false,
  entity: null,
  updating: false,
  updateSuccess: false,
  rows: [],
  rowsTotal: 0,
  loadingRows: false,
  maxImplementCount: 0,
  row: null,
  loadingRow: false,
  summary: null,
  loadingSummary: false,
  summaryError: false,
  savingCoupling: false,
  saveCouplingSuccess: false,
};

export const getEligibleDrivers = createAsyncThunk(
  'driverAssignment/fetch_eligible_drivers',
  async (companyId: number) => {
    const response = await axios.get<IEligibleDriver[]>(`${apiUrl}/eligible-drivers`, { params: { companyId } });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getDriverAssignment = createAsyncThunk(
  'driverAssignment/fetch_one',
  async (id: number | string) => {
    const response = await axios.get<IDriverAssignmentListItem>(`${apiUrl}/${id}`);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const deleteDriverAssignment = createAsyncThunk(
  'driverAssignment/delete',
  async (id: number | string) => {
    const response = await axios.delete<void>(`${apiUrl}/${id}`);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

// ---- Unified coupling screen ----

export interface CouplingRowsQuery {
  companyId?: number | null;
  licensePlate?: string | null;
  driverName?: string | null;
  page?: number;
  size?: number;
  sort?: string;
}

export const MAX_IMPLEMENT_COUNT_HEADER = 'x-max-implement-count';

// The header is computed over the whole filter (not the page), so the implement columns stay stable across pages.
export const parseMaxImplementCount = (headers?: Record<string, any> | null): number => {
  const value = Number(headers?.[MAX_IMPLEMENT_COUNT_HEADER]);
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
};

export const getCouplingRows = createAsyncThunk(
  'driverAssignment/fetch_coupling_rows',
  async ({ companyId, licensePlate, driverName, page = 0, size = 20, sort = 'validFrom,desc' }: CouplingRowsQuery) => {
    // Both search strings always travel: a null String would bind as bytea in the native backend query.
    const params: Record<string, string | number> = {
      page,
      size,
      sort,
      searchLicensePlate: licensePlate?.trim() ?? '',
      searchDriverName: driverName?.trim() ?? '',
    };
    if (companyId != null) {
      params.searchCompanyId = companyId;
    }
    const response = await axios.get<any>(`${apiUrl}/couplings`, { params });
    return { page: response.data, maxImplementCount: parseMaxImplementCount(response.headers) };
  },
  { serializeError: serializeAxiosError }
);

export const getCouplingRow = createAsyncThunk(
  'driverAssignment/fetch_coupling_row',
  async (couplingId: number | string) => {
    const response = await axios.get<ICouplingAssignmentRow>(`${apiUrl}/couplings/${couplingId}`);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const saveCouplingAssignment = createAsyncThunk(
  'driverAssignment/save_coupling',
  async ({ couplingId, payload }: { couplingId?: number | string | null; payload: IDriverAssignmentComposition }) => {
    const response =
      couplingId != null
        ? await axios.put<ICouplingAssignmentRow>(`${apiUrl}/couplings/${couplingId}`, payload)
        : await axios.post<ICouplingAssignmentRow>(`${apiUrl}/couplings`, payload);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const deleteCouplingAssignment = createAsyncThunk(
  'driverAssignment/delete_coupling',
  async ({ couplingId, scope }: { couplingId: number | string; scope: CouplingDeleteScope }) => {
    const response = await axios.delete<void>(`${apiUrl}/couplings/${couplingId}`, { params: { scope } });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getUnassignedSummary = createAsyncThunk(
  'driverAssignment/fetch_unassigned_summary',
  async (companyId?: number | null) => {
    const params: Record<string, number> = {};
    if (companyId != null) {
      params.searchCompanyId = companyId;
    }
    const response = await axios.get<IUnassignedSummary>(`${apiUrl}/unassigned-summary`, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

const driverAssignmentSlice = createSlice({
  name: 'driverAssignment',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    resetCouplingRow(state) {
      state.row = null;
      state.loadingRow = false;
      state.savingCoupling = false;
      state.saveCouplingSuccess = false;
      state.errorKey = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEligibleDrivers.pending, state => {
        state.loadingDrivers = true;
        state.errorMessage = null;
      })
      .addCase(getEligibleDrivers.fulfilled, (state, action) => {
        state.loadingDrivers = false;
        state.eligibleDrivers = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getEligibleDrivers.rejected, (state, action) => {
        state.loadingDrivers = false;
        state.eligibleDrivers = [];
        state.errorMessage = action.error.message || 'Error fetching eligible drivers';
      })
      .addCase(getDriverAssignment.pending, state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(getDriverAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload ?? null;
      })
      .addCase(getDriverAssignment.rejected, (state, action) => {
        state.loading = false;
        state.entity = null;
        state.errorMessage = action.error.message || 'Error fetching driver assignment';
      })
      .addCase(getCouplingRows.pending, state => {
        state.loadingRows = true;
        state.errorMessage = null;
      })
      .addCase(getCouplingRows.fulfilled, (state, action) => {
        state.loadingRows = false;
        state.rows = action.payload?.page?.content ?? [];
        state.rowsTotal = action.payload?.page?.totalElements ?? 0;
        state.maxImplementCount = action.payload?.maxImplementCount ?? 0;
      })
      .addCase(getCouplingRows.rejected, (state, action) => {
        state.loadingRows = false;
        state.rows = [];
        state.rowsTotal = 0;
        state.maxImplementCount = 0;
        state.errorMessage = action.error.message || 'Error fetching couplings';
      })
      .addCase(getCouplingRow.pending, state => {
        // Cleared so an edit never shows the row of a previous edit while loading.
        state.loadingRow = true;
        state.row = null;
      })
      .addCase(getCouplingRow.fulfilled, (state, action) => {
        state.loadingRow = false;
        state.row = action.payload ?? null;
      })
      .addCase(getCouplingRow.rejected, (state, action) => {
        state.loadingRow = false;
        state.row = null;
        state.errorMessage = action.error.message || 'Error fetching coupling';
      })
      .addCase(saveCouplingAssignment.pending, state => {
        state.savingCoupling = true;
        state.saveCouplingSuccess = false;
        state.errorKey = null;
        state.errorMessage = null;
      })
      .addCase(saveCouplingAssignment.fulfilled, (state, action) => {
        state.savingCoupling = false;
        state.saveCouplingSuccess = true;
        state.row = action.payload ?? null;
      })
      .addCase(saveCouplingAssignment.rejected, (state, action) => {
        state.savingCoupling = false;
        state.saveCouplingSuccess = false;
        // The backend problem+json carries the key as message 'error.<errorKey>'.
        state.errorKey = extractErrorKey(action.error);
        state.errorMessage = action.error.message || 'Error saving coupling';
      })
      .addCase(deleteCouplingAssignment.pending, state => {
        state.updating = true;
        state.updateSuccess = false;
        state.errorKey = null;
        state.errorMessage = null;
      })
      .addCase(deleteCouplingAssignment.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addCase(deleteCouplingAssignment.rejected, (state, action) => {
        state.updating = false;
        state.updateSuccess = false;
        state.errorKey = extractErrorKey(action.error);
        state.errorMessage = action.error.message || 'Error deleting coupling';
      })
      .addCase(getUnassignedSummary.pending, state => {
        state.loadingSummary = true;
        state.summaryError = false;
      })
      .addCase(getUnassignedSummary.fulfilled, (state, action) => {
        state.loadingSummary = false;
        state.summary = action.payload ?? null;
      })
      .addCase(getUnassignedSummary.rejected, state => {
        state.loadingSummary = false;
        state.summary = null;
        state.summaryError = true;
      })
      .addCase(deleteDriverAssignment.pending, state => {
        state.updating = true;
        state.updateSuccess = false;
        state.errorKey = null;
        state.errorMessage = null;
      })
      .addCase(deleteDriverAssignment.fulfilled, (state, action: any) => {
        state.updating = false;
        state.updateSuccess = true;
        if (action.payload && action.payload.id) {
          state.entity = action.payload;
        }
      })
      .addCase(deleteDriverAssignment.rejected, (state, action: any) => {
        state.updating = false;
        state.updateSuccess = false;
        const data = action.error?.response?.data;
        state.errorKey = data?.errorKey ?? null;
        state.errorMessage = action.error?.message || 'Error saving driver assignment';
      });
  },
});

export const { reset, resetCouplingRow } = driverAssignmentSlice.actions;

export default driverAssignmentSlice.reducer;
